import React from 'react';
import { css } from 'emotion';
import Helmet from 'react-helmet';
import Link from 'gatsby-link';
import Header from '../components/appComponents/Header';
import { CUSTOMER_APP } from '../appLinks';

/* global tw */

const CONTACT = {
    phone: '+91 8688 901 902',
    phoneHref: 'tel:+918688901902',
    supportEmail: 'support@zyppys.com',
    whatsapp: 'https://wa.me/918374108306?text=' + encodeURIComponent("Hi Zyppys, I'd like help with a booking."),
};

const styles = {
    page: css`
        ${tw`w-screen relative`};
        background: #0A0A0F;
        background-image:
            radial-gradient(ellipse 70% 50% at 50% 0%, rgba(170,11,202,0.12), transparent 60%),
            radial-gradient(ellipse 40% 30% at 88% 22%, rgba(177,92,255,0.06), transparent 70%);
        font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
        min-height: 100vh;
        padding-bottom: 6rem;
    `,
    container: css`
        max-width: 78rem;
        ${tw`mx-auto px-6 lg:px-10`};
    `,
    heroInner: css`
        max-width: 78rem;
        ${tw`mx-auto px-6 lg:px-10`};
        padding-top: 7rem;
        padding-bottom: 1rem;
        @media (min-width: 992px) {
            padding-top: 9rem;
        }
    `,
    eyebrow: css`
        display: inline-flex;
        align-items: center;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.22em;
        font-size: 0.75rem;
        color: #C9A2FF;
        padding: 0.45rem 0.9rem;
        border: 1px solid rgba(201, 162, 255, 0.4);
        border-radius: 9999px;
        background: rgba(201, 162, 255, 0.06);
        margin-bottom: 1.25rem;
    `,
    h1: css`
        font-weight: 800;
        font-size: 2.5rem;
        color: #F5F5F7;
        letter-spacing: -0.035em;
        line-height: 1.1;
        margin: 0 0 1rem 0;
        max-width: 42rem;
        @media (min-width: 992px) { font-size: 3.5rem; }
        span {
            background: linear-gradient(135deg, #FFFFFF 0%, #CB53D1 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
        }
    `,
    lede: css`
        font-size: 1.0625rem;
        color: #BFBFD1;
        line-height: 1.7;
        max-width: 42rem;
        margin: 1rem 0 2rem 0;
        @media (min-width: 992px) { font-size: 1.125rem; }
    `,
    heroCtas: css`
        ${tw`flex flex-wrap items-stretch`};
        gap: 0.75rem;
        margin-bottom: 1.5rem;
    `,
    primaryCta: css`
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: #fff;
        text-decoration: none;
        font-weight: 700;
        font-size: 1rem;
        letter-spacing: -0.005em;
        padding: 1rem 1.75rem;
        border-radius: 9999px;
        background: linear-gradient(135deg, #AA0BCA 0%, #5500DB 100%);
        box-shadow: 0 8px 22px rgba(170, 11, 202, 0.4), inset 0 1px 0 rgba(255,255,255,0.15);
        transition: all 0.2s ease;
        cursor: pointer;
        border: 0;
        &:hover {
            transform: translateY(-2px);
            filter: brightness(1.15);
            box-shadow: 0 0 24px rgba(170, 11, 202, 0.55), inset 0 1px 0 rgba(255,255,255,0.15);
        }
        &::after {
            content: '\\2192';
            margin-left: 0.55rem;
            font-size: 1.05em;
            transition: transform 0.2s ease;
        }
        &:hover::after { transform: translateX(4px); }
    `,
    whatsappCta: css`
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: #E8FFF2 !important;
        text-decoration: none;
        font-weight: 700;
        font-size: 1rem;
        letter-spacing: -0.005em;
        padding: 1rem 1.75rem;
        border-radius: 9999px;
        background: rgba(37, 211, 102, 0.10);
        border: 1px solid rgba(37, 211, 102, 0.45);
        transition: all 0.2s ease;
        svg { margin-right: 0.6rem; flex-shrink: 0; }
        &:hover {
            background: rgba(37, 211, 102, 0.18);
            border-color: rgba(37, 211, 102, 0.75);
            transform: translateY(-2px);
            box-shadow: 0 10px 26px rgba(37, 211, 102, 0.18);
        }
    `,
    secondaryCta: css`
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: #EFE2FF;
        text-decoration: none;
        font-weight: 600;
        font-size: 0.9375rem;
        letter-spacing: -0.005em;
        padding: 1rem 1.5rem;
        border-radius: 9999px;
        background: rgba(201, 162, 255, 0.06);
        border: 1px solid rgba(201, 162, 255, 0.4);
        transition: all 0.2s ease;
        &::before {
            content: '';
            display: inline-block;
            width: 7px;
            height: 7px;
            border-radius: 9999px;
            background: #C9A2FF;
            margin-right: 0.55rem;
            box-shadow: 0 0 0 4px rgba(201,162,255,0.18);
        }
        &:hover {
            background: rgba(201, 162, 255, 0.14);
            border-color: rgba(201, 162, 255, 0.7);
            color: #fff;
        }
    `,
    pillRow: css`
        ${tw`flex flex-wrap`};
        gap: 0.5rem;
        max-width: 60rem;
        margin-top: 0.5rem;
    `,
    pill: css`
        display: inline-flex;
        align-items: center;
        padding: 0.5rem 0.875rem;
        border-radius: 9999px;
        background: rgba(255, 255, 255, 0.04);
        border: 1px solid rgba(255, 255, 255, 0.10);
        color: #F0ECF9;
        font-size: 0.875rem;
        font-weight: 500;
        letter-spacing: -0.005em;
    `,
    section: css`
        ${tw`mt-14 lg:mt-20`};
    `,
    h2: css`
        font-weight: 800;
        font-size: 1.75rem;
        color: #F5F5F7;
        letter-spacing: -0.025em;
        line-height: 1.15;
        margin: 0 0 1rem 0;
        @media (min-width: 992px) { font-size: 2.25rem; }
        &::after {
            content: '';
            display: block;
            margin-top: 0.875rem;
            height: 2px;
            width: 2.5rem;
            background: #C9A2FF;
            border-radius: 2px;
        }
    `,
    p: css`
        font-size: 1rem;
        color: #9898A8;
        line-height: 1.75;
        margin: 0.75rem 0;
        max-width: 52rem;
    `,
    cardGrid: css`
        ${tw`flex flex-wrap -mx-2 mt-6`};
    `,
    card: css`
        ${tw`mx-2 mb-4 p-5`};
        flex: 1 1 16rem;
        background: rgba(255, 255, 255, 0.025);
        border: 1px solid rgba(255, 255, 255, 0.06);
        border-radius: 14px;
        transition: all 0.25s ease;
        &:hover {
            border-color: rgba(255, 255, 255, 0.12);
            background: rgba(255, 255, 255, 0.04);
            transform: translateY(-2px);
            box-shadow: 0 12px 30px rgba(0, 0, 0, 0.35);
        }
        h5 {
            margin: 0 0 0.5rem 0;
            font-weight: 700;
            font-size: 1.0625rem;
            color: #F5F5F7;
            letter-spacing: -0.01em;
        }
        p {
            margin: 0;
            font-size: 0.9375rem;
            color: #9898A8;
            line-height: 1.65;
        }
        a {
            display: inline-block;
            margin-top: 0.75rem;
            color: #C9A2FF;
            font-size: 0.875rem;
            font-weight: 600;
            text-decoration: none;
            &:hover { color: #F0ECF9; }
        }
    `,
    splitGrid: css`
        ${tw`flex flex-wrap -mx-2 mt-6`};
    `,
    splitCard: css`
        ${tw`mx-2 mb-4 p-6`};
        flex: 1 1 22rem;
        background: linear-gradient(135deg, rgba(255, 255, 255, 0.03) 0%, rgba(170, 11, 202, 0.05) 100%);
        border: 1px solid rgba(255, 255, 255, 0.08);
        border-radius: 18px;
        h3 {
            margin: 0 0 0.75rem 0;
            font-weight: 800;
            font-size: 1.25rem;
            color: #C9A2FF;
            letter-spacing: -0.02em;
        }
        p {
            margin: 0;
            font-size: 0.9375rem;
            color: #BFBFD1;
            line-height: 1.75;
        }
    `,
    contactCard: css`
        ${tw`mt-6 p-6 lg:p-8`};
        background: linear-gradient(135deg, rgba(255, 255, 255, 0.03) 0%, rgba(170, 11, 202, 0.04) 100%);
        border: 1px solid rgba(255, 255, 255, 0.08);
        border-radius: 20px;
    `,
    contactGrid: css`
        ${tw`flex flex-wrap -mx-2 mt-4`};
    `,
    contactItem: css`
        ${tw`mx-2 mb-4 p-5`};
        flex: 1 1 15rem;
        background: rgba(10, 10, 15, 0.45);
        border: 1px solid rgba(255, 255, 255, 0.07);
        border-radius: 14px;
        transition: all 0.2s ease;
        &:hover {
            border-color: rgba(201, 162, 255, 0.35);
            transform: translateY(-2px);
        }
        .label {
            display: block;
            font-weight: 700;
            font-size: 0.6875rem;
            letter-spacing: 0.14em;
            text-transform: uppercase;
            color: #C9A2FF;
            margin-bottom: 0.55rem;
        }
        a {
            display: inline-flex;
            align-items: center;
            color: #F5F5F7;
            font-weight: 700;
            font-size: 1rem;
            letter-spacing: -0.01em;
            text-decoration: none;
            word-break: break-word;
            svg { margin-right: 0.5rem; flex-shrink: 0; }
            &:hover { color: #C9A2FF; }
        }
        .note {
            margin: 0.5rem 0 0 0;
            font-size: 0.8125rem;
            color: #9898A8;
            line-height: 1.6;
        }
        address {
            font-style: normal;
            color: #BFBFD1;
            font-size: 0.9375rem;
            line-height: 1.7;
        }
    `,
    closing: css`
        ${tw`mt-16 text-center`};
        h3 {
            font-weight: 800;
            font-size: 1.5rem;
            color: #F5F5F7;
            letter-spacing: -0.02em;
            margin: 0 0 0.5rem 0;
        }
        .brand {
            font-weight: 800;
            font-size: 2rem;
            background: linear-gradient(135deg, #FFFFFF 0%, #CB53D1 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            margin: 1.25rem 0 0.25rem 0;
        }
        .tagline {
            color: #9898A8;
            font-size: 1rem;
            font-style: italic;
        }
    `,
};

const WhatsAppIcon = ({ size = 18, color = '#25D366' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} aria-hidden="true">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.149-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347M12.05 21.785h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413" />
    </svg>
);

const MailIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#C9A2FF" strokeWidth="1.8" aria-hidden="true">
        <rect x="2.5" y="4.5" width="19" height="15" rx="2.5" />
        <path d="M3 6.5l9 6 9-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const PhoneIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#C9A2FF" strokeWidth="1.8" aria-hidden="true">
        <path d="M6.5 3.5h3l1.5 4-2 1.5a12 12 0 0 0 6 6l1.5-2 4 1.5v3a2 2 0 0 1-2.2 2A16.5 16.5 0 0 1 4.5 5.7 2 2 0 0 1 6.5 3.5z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const SERVICES = [
    { title: 'Airport Transfers', desc: 'Premium pickups and drops timed around your flight, with a chauffeur who is already there when you land.' },
    { title: 'Outstation Journeys', desc: 'Intercity travel across India, one way or round trip, planned around your itinerary rather than a meter.' },
    { title: 'Hourly Rentals', desc: 'A chauffeur-driven car at your disposal for meetings, shoots, shopping or a full day across the city.' },
    { title: 'Weddings & Events', desc: 'Multi-vehicle coordination for the family and guests, across days and venues, run by one concierge.', to: '/concierge/', linkText: 'Explore Concierge' },
];

const REASONS = [
    { title: 'A curated fleet', desc: 'Premium sedans, SUVs and luxury vehicles, inspected and maintained, so the car that arrives is the car you booked.' },
    { title: 'Professional chauffeurs', desc: 'Verified, trained and briefed on your trip. Courteous, discreet, and familiar with the routes they drive.' },
    { title: 'Transparent pricing', desc: 'Clear, all-in quotes with no hidden extras. You see what a journey costs before you confirm it.' },
    { title: 'Booking in minutes', desc: 'Compare cars and confirm on the website or the Zyppys app, with your itinerary saved and trackable.' },
    { title: 'Safety as standard', desc: 'Regular vehicle checks, chauffeur verification and live trip visibility for you and the people expecting you.' },
    { title: 'Support that answers', desc: 'A team on WhatsApp, email and phone around the clock, from the first enquiry until the trip is complete.' },
];

class AboutUs extends React.Component {
    render() {
        return (
            <div className={styles.page}>
                <Helmet
                    title="About Zyppys — Premium Chauffeur-Driven Car Service in India"
                    meta={[
                        { name: 'description', content: 'Zyppys is a premium chauffeur-driven car service in India offering airport transfers, outstation journeys, hourly rentals and event concierge, with vetted chauffeurs, a curated fleet and 24/7 support.' },
                        { name: 'keywords', content: 'about Zyppys, chauffeur driven car rental India, luxury car rental company, airport transfer service, outstation cabs, hourly car rental, premium car service Hyderabad' },
                        { name: 'viewport', content: 'width=device-width, initial-scale=1, shrink-to-fit=no' },
                        { property: 'og:title', content: 'About Zyppys — Premium Chauffeur-Driven Car Service in India' },
                        { property: 'og:description', content: 'Who we are, what we run, and how to reach us — airport transfers, outstation journeys, hourly rentals and event concierge across India.' },
                        { property: 'og:type', content: 'website' },
                        { property: 'og:site_name', content: 'Zyppys' },
                        { property: 'og:image', content: 'https://zyppysimages.s3.ap-south-1.amazonaws.com/zyplogo.png' },
                    ]}
                    link={[
                        { rel: 'canonical', href: 'https://zyppys.com/aboutus/' },
                        { rel: 'icon', type: 'image/png', href: '/favicon.png' },
                    ]}
                />
                <Header iosUrl={CUSTOMER_APP.ios} androidUrl={CUSTOMER_APP.android} />

                <div className={styles.heroInner}>
                    <div className={styles.eyebrow}>&#9733; &nbsp;About Zyppys</div>
                    <h1 className={styles.h1}>Not a car rental. <span>A guest transportation concierge.</span></h1>
                    <p className={styles.lede}>
                        Zyppys is a premium chauffeur-driven car service for people who care how they arrive.
                        A curated fleet, chauffeurs we vet ourselves, and a team that stays with the trip
                        until the last guest is home.
                    </p>
                    <div className={styles.heroCtas}>
                        <a className={styles.whatsappCta} href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer">
                            <WhatsAppIcon /> Chat on WhatsApp
                        </a>
                        <a className={styles.secondaryCta} href={CONTACT.phoneHref}>Call us &middot; {CONTACT.phone}</a>
                    </div>
                    <div className={styles.pillRow}>
                        <span className={styles.pill}>Chauffeur-driven only</span>
                        <span className={styles.pill}>Airport &middot; Outstation &middot; Hourly</span>
                        <span className={styles.pill}>Pan-India coverage</span>
                        <span className={styles.pill}>24/7 support</span>
                    </div>
                </div>

                <div className={styles.container}>
                    <section className={styles.section}>
                        <h2 className={styles.h2}>Who we are</h2>
                        <p className={styles.p}>
                            Booking a car in India has long been a gamble &mdash; an unfamiliar vehicle, a price that
                            moves after the trip, and nobody to call when plans change. Zyppys was built to take
                            that guesswork out. We bring a vetted supply of premium vehicles and chauffeurs onto one
                            platform, quote a clear all-in price up front, and keep a real team reachable for the
                            whole journey.
                        </p>
                        <p className={styles.p}>
                            Today that covers everything from a single airport run to the entire car operation for a
                            multi-day wedding. Zyppys is headquartered at T-Hub, Hyderabad, and operates as an entity
                            of Twilight IT Professionals Private Limited.
                        </p>
                    </section>

                    <section className={styles.section}>
                        <h2 className={styles.h2}>What we run</h2>
                        <p className={styles.p}>Four ways to travel with us, all chauffeur-driven.</p>
                        <div className={styles.cardGrid}>
                            {SERVICES.map(item => (
                                <div key={item.title} className={styles.card}>
                                    <h5>{item.title}</h5>
                                    <p>{item.desc}</p>
                                    {item.to && <Link to={item.to}>{item.linkText} &rarr;</Link>}
                                </div>
                            ))}
                        </div>
                    </section>

                    <section className={styles.section}>
                        <h2 className={styles.h2}>Why guests choose Zyppys</h2>
                        <div className={styles.cardGrid}>
                            {REASONS.map(item => (
                                <div key={item.title} className={styles.card}>
                                    <h5>{item.title}</h5>
                                    <p>{item.desc}</p>
                                </div>
                            ))}
                        </div>
                    </section>

                    <section className={styles.section}>
                        <h2 className={styles.h2}>Mission &amp; vision</h2>
                        <div className={styles.splitGrid}>
                            <div className={styles.splitCard}>
                                <h3>Our mission</h3>
                                <p>
                                    To make premium travel effortless &mdash; matching every guest with the right
                                    vehicle and the right chauffeur, at a price they can see in advance, and standing
                                    behind the journey from booking to drop.
                                </p>
                            </div>
                            <div className={styles.splitCard}>
                                <h3>Our vision</h3>
                                <p>
                                    To be the name India trusts for chauffeur-driven travel &mdash; setting the
                                    standard for service quality, safety and transparency, so that arriving well is
                                    never something you have to arrange twice.
                                </p>
                            </div>
                        </div>
                    </section>

                    <section className={styles.section} id="contact">
                        <h2 className={styles.h2}>Talk to us</h2>
                        <p className={styles.p}>
                            Planning a trip, changing one, or just want a quote? Reach us whichever way suits you.
                        </p>
                        <div className={styles.contactCard}>
                            <div className={styles.contactGrid}>
                                <div className={styles.contactItem}>
                                    <span className="label">WhatsApp</span>
                                    <a href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer">
                                        <WhatsAppIcon /> Message us
                                    </a>
                                    <p className="note">Fastest way to reach a human. Share your dates and we will quote.</p>
                                </div>
                                <div className={styles.contactItem}>
                                    <span className="label">Support email</span>
                                    <a href={'mailto:' + CONTACT.supportEmail}>
                                        <MailIcon /> {CONTACT.supportEmail}
                                    </a>
                                    <p className="note">Booking help, invoices and anything that needs a paper trail.</p>
                                </div>
                                <div className={styles.contactItem}>
                                    <span className="label">Phone</span>
                                    <a href={CONTACT.phoneHref}>
                                        <PhoneIcon /> {CONTACT.phone}
                                    </a>
                                    <p className="note">Available 24/7 for live trips and urgent changes.</p>
                                </div>
                                <div className={styles.contactItem}>
                                    <span className="label">Office</span>
                                    <address>
                                        T-Hub<br />
                                        Gachibowli, Hyderabad<br />
                                        Telangana &mdash; 500081
                                    </address>
                                </div>
                            </div>
                        </div>
                    </section>

                    <div className={styles.closing}>
                        <h3>Premium mobility, handled.</h3>
                        <a className={styles.primaryCta} href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer">Start a conversation</a>
                        <div className="brand">Zyppys</div>
                        <div className="tagline">Driven Beautifully.</div>
                    </div>
                </div>
            </div>
        );
    }
}

export default AboutUs;
